/**
 * Documents project context behavior for ERP Accelerator.
 */

"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';

interface ProjectContextType {
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  projects: any[];
  loading: boolean;
  error: string | null;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

/**
 * Handles project provider behavior.
 */
export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load projects on initial render
  useEffect(() => {
    const storedProjectId = localStorage.getItem('activeProjectId');
    if (storedProjectId) {
      setActiveProjectId(storedProjectId);
    }
    setProjects([]);
    setError(null);
    setLoading(false);
  }, []);

  // Update localStorage when active project changes
  useEffect(() => {
    if (activeProjectId) {
      localStorage.setItem('activeProjectId', activeProjectId.toString());
    } else {
      localStorage.removeItem('activeProjectId');
    }
  }, [activeProjectId]);

  const value = {
    activeProjectId,
    setActiveProjectId,
    projects,
    loading,
    error
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

/**
 * Handles use project behavior.
 */
export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
